/**
 *
 * ListError
 *
 * The state a fetched list lands in when its request FAILED, as opposed to the
 * one where it resolved to nothing.
 *
 * The dashboard lists used to have only two terminal states — a table, or
 * "It's looking empty around here…" — and routed three conditions into two, so
 * a user whose channels 500'd was told they had none and invited to make their
 * first. This is the third state, and it exists to say the opposite: nothing
 * has been lost, the server just could not be reached.
 *
 * It is also the signal the e2e suite reads. Several specs decide `test.skip()`
 * from an empty list, so while a failed fetch rendered the same DOM as an empty
 * one, a server error was indistinguishable from a legitimately empty account
 * and skipped silently. `waitForAppReady` now refuses to return while
 * `data-testid="list-error"` is on the page — which is only possible because
 * the two states are distinguishable at all. Keep the testid if the copy
 * changes; the copy is not what the suite matches on.
 */

import React from 'react';
import { colors } from 'globalStyles';
import Icon from 'components/Icon';
import {
  StyledListError,
  ErrorTitle,
  ErrorText,
  RetryButton,
} from './styledComponents';

const ListError = ({ title, onRetry, isRetrying }) => (
  <StyledListError role="alert" data-testid="list-error">
    {/* Decorative: `role="alert"` announces the copy below, and an unlabelled
        glyph would only add noise to that announcement. Icon renders a bare
        <svg> and forwards no ARIA, so the wrapper carries it. The `info` path
        is drawn on a 24-unit grid, unlike Icon's 32-unit default. */}
    <span aria-hidden="true">
      <Icon size="72" viewBox="0 0 24 24" name="info" fill={colors.DANGER_FG} />
    </span>
    <ErrorTitle>{title}</ErrorTitle>
    <ErrorText>
      Something went wrong on our end — this doesn’t mean there’s nothing here.
      Try again, or reload the page.
    </ErrorText>
    <RetryButton type="button" onClick={onRetry} disabled={isRetrying}>
      {isRetrying ? 'Retrying…' : 'Try again'}
    </RetryButton>
  </StyledListError>
);

export default ListError;
